import React, {useState, useEffect} from 'react'
import styles from './DailyMonitoringDetail.module.css'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import axios from 'axios'
import {FaCircleArrowLeft} from 'react-icons/fa6'

import {useAuth} from '../../../modules/auth'

interface DailyMonitoringDetailProps {
  details: any
  onBack?: () => void
}

const DailyMonitoringDetail = ({details, onBack}: DailyMonitoringDetailProps) => {
  const user = useAuth()
  const {auth} = useAuth()
  const [showPreview, setShowPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewFileName, setPreviewFileName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loadingDocId, setLoadingDocId] = useState<number | null>(null)
  const [icdNames, setIcdNames] = useState<{[key: string]: string}>({})

  const fetchIcdName = async (icdCode: string) => {
    if (!icdCode || icdCode === '-') return

    try {
      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/MstIcd10/GetPaginatedMstIcd10s?searchTerm=${icdCode}&pageNumber=1&pageSize=10`
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
        },
      })
      const data = response.data.data || []

      const matchedIcd = data.find((icd: any) => icd.code === icdCode)
      if (matchedIcd) {
        setIcdNames((prev) => ({
          ...prev,
          [icdCode]: matchedIcd.name,
        }))
      }
    } catch (err) {
      console.error('Error fetching ICD-10 name:', err)
    }
  }

  useEffect(() => {
    if (details?.data?.detailMonitoring) {
      const {icdCode1, icdCode2, icdCode3, icdCode4} = details.data.detailMonitoring
      ;[icdCode1, icdCode2, icdCode3, icdCode4].forEach((code) => {
        if (code) fetchIcdName(code)
      })
    }
  }, [details])

  if (!details) {
    return <div>Loading...</div>
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)

      if (isNaN(date.getTime())) {
        return 'Invalid Date'
      }

      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }

  const displayDate = formatDate(details.data.detailMonitoring.monitoringDate)

  console.log('Monitoring Date:', details.monitoringDate)
  console.log('Formatted Date:', displayDate)

  const handleDocumentClick = async (documentName: string, index: number) => {
    try {
      setLoadingDocId(index)
      const claimNumber = details.data.detailMonitoring.claimNumber
      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/GetFile?claimNumber=${claimNumber}&fileName=${documentName}`
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
        },
      })

      if (!response.data.isSuccess) {
        throw new Error('Failed to fetch document')
      }

      const data = response.data
      console.log('API Response:', data) // Debug log

      if (data.isSuccess && data.data.url) {
        // Check if URL needs encoding
        let fileUrl = data.data.url

        // Log the URL we're trying to open
        console.log('Attempting to open URL:', fileUrl)

        // Try to decode and re-encode the URL properly
        try {
          const decodedUrl = decodeURIComponent(fileUrl)
          const encodedUrl = encodeURI(decodedUrl)
          console.log('Encoded URL:', encodedUrl)
          window.open(encodedUrl, '_blank')
        } catch (urlError) {
          console.error('Error processing URL:', urlError)
          // Fallback to original URL if encoding fails
          window.open(fileUrl, '_blank')
        }
      } else {
        console.error('API Response Error:', data)
        toast.error('Failed to get document URL')
      }
    } catch (error) {
      console.error('Error fetching document:', error)
      toast.error('Error loading document')
    } finally {
      setLoadingDocId(null)
    }
  }

  const handleClosePreview = () => {
    setShowPreview(false)
    setPreviewUrl('')
  }

  const handleDocumentPreview = async (documentName: string, index: number) => {
    try {
      setLoadingDocId(index)
      const claimNumber = details.data.detailMonitoring.claimNumber

      const documentUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/GetFile?claimNumber=${claimNumber}&fileName=${documentName}`
      const previewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(documentUrl)}`
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }

      console.log('Preview URL:', previewUrl)

      window.open(previewUrl, '_blank')
    } catch (error) {
      console.error('Error opening preview:', error)
      toast.error('Error opening preview')
    } finally {
      setLoadingDocId(null)
    }
  }

  return (
    <div className={styles.container}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          marginTop: '25px',
        }}
      >
        <FaCircleArrowLeft
          size={18}
          onClick={onBack}
          style={{
            cursor: 'pointer',
            gap: '6px',
            position: 'absolute',
            top: '30px',
            left: '55px',
          }}
        />

        <h2 style={{margin: 0, marginTop: '25px'}}>{displayDate}</h2>
      </div>
      <div className={styles.section}>
        <div className={styles.row} style={{paddingTop: '30px'}}>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>ICD10 - 1</strong>
            <p>
              {details.data.detailMonitoring.icdCode1 ? (
                <>
                  {details.data.detailMonitoring.icdCode1}
                  {icdNames[details.data.detailMonitoring.icdCode1] && (
                    <span style={{display: 'block', fontSize: '0.9em', color: '#666'}}>
                      {icdNames[details.data.detailMonitoring.icdCode1]}
                    </span>
                  )}
                </>
              ) : (
                '-'
              )}
            </p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>ICD10 - 2</strong>
            <p>
              {details.data.detailMonitoring.icdCode2 ? (
                <>
                  {details.data.detailMonitoring.icdCode2}
                  {icdNames[details.data.detailMonitoring.icdCode2] && (
                    <span style={{display: 'block', fontSize: '0.9em', color: '#666'}}>
                      {icdNames[details.data.detailMonitoring.icdCode2]}
                    </span>
                  )}
                </>
              ) : (
                '-'
              )}
            </p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>ICD10 - 3</strong>
            <p>
              {details.data.detailMonitoring.icdCode3 ? (
                <>
                  {details.data.detailMonitoring.icdCode3}
                  {icdNames[details.data.detailMonitoring.icdCode3] && (
                    <span style={{display: 'block', fontSize: '0.9em', color: '#666'}}>
                      {icdNames[details.data.detailMonitoring.icdCode3]}
                    </span>
                  )}
                </>
              ) : (
                '-'
              )}
            </p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>ICD10 - 4</strong>
            <p>
              {details.data.detailMonitoring.icdCode4 ? (
                <>
                  {details.data.detailMonitoring.icdCode4}
                  {icdNames[details.data.detailMonitoring.icdCode4] && (
                    <span style={{display: 'block', fontSize: '0.9em', color: '#666'}}>
                      {icdNames[details.data.detailMonitoring.icdCode4]}
                    </span>
                  )}
                </>
              ) : (
                '-'
              )}
            </p>
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Laboratorium</strong>
            <p>{details.data.detailMonitoring.laboratory || '-'}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>General Condition</strong>
            <p>{details.data.detailMonitoring.generalCondition || '-'}</p>
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Doctor</strong>
            <p>{details.data.detailMonitoring.doctor || '-'}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Specialist</strong>
            <p>{details.data.detailMonitoring.specialist || '-'}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Medical Expense</strong>
            <p>{details.data.detailMonitoring.medicalExpense || '-'}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Upgrade Room</strong>
            <p>{details.data.detailMonitoring.upgradeRoom || '-'}</p>
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Reason</strong>
            <p>{details.data.detailMonitoring.reason || '-'}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Is APS</strong>
            <p>{details.data.detailMonitoring.isAps ? 'Yes' : 'No'}</p>
          </div>
          {/* <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Cost Saving</strong>
            <p>{details.data.detailMonitoring.costSaving}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Remarks Cost Saving</strong>
            <p>{details.data.detailMonitoring.remarkCostSaving}</p>
          </div> */}
        </div>

        <div className={styles.row}>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Pulse</strong>
            <p>{details.data.detailMonitoring.pulse}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Respiratory</strong>
            <p>{details.data.detailMonitoring.respiratory}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Blood Pressure</strong>
            <p>{details.data.detailMonitoring.bloodPressure}mmHg</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Temperature</strong>
            <p>{details.data.detailMonitoring.temperature}°C</p>
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Supporting</strong>
            <p>{details.data.detailMonitoring.supporting}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Follow Up</strong>
            <p>{details.data.detailMonitoring.followUp}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Treatment</strong>
            <p>{details.data.detailMonitoring.treatment}</p>
          </div>
          <div className={styles.column}>
            <strong style={{color: '#17828F'}}>Remark</strong>
            <p>{details.data.detailMonitoring.remark}</p>
          </div>
        </div>
      </div>

      <div className={styles.documents}>
        <h3 style={{color: '#17828F'}}>Documents</h3>
        <div className={styles.docList}>
          {details.data.documents.map((doc, index) => (
            <div key={index} className={styles.docItem}>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span
                  onClick={() => handleDocumentClick(doc.documentName, index)}
                  style={{
                    cursor: 'pointer',
                    color: '#0d6efd',
                    textDecoration: 'underline',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {doc.documentName}
                  {loadingDocId === index && (
                    <span
                      className='spinner-border spinner-border-sm'
                      role='status'
                      aria-hidden='true'
                    />
                  )}
                </span>
                {/* Add preview button */}
              </div>
              {/* <button className={styles.removeButton}>X</button> */}
            </div>
          ))}
        </div>
      </div>

      <Modal show={showPreview} onHide={handleClosePreview} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>{previewFileName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className='p-0' style={{height: '80vh'}}>
          {isLoading ? (
            <div className='d-flex justify-content-center align-items-center h-100'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <div className='d-flex justify-content-center align-items-center h-100'>
              <button className='btn btn-primary' onClick={() => window.open(previewUrl, '_blank')}>
                Open Document in New Tab
              </button>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default DailyMonitoringDetail
